/**
 * Canvas APIを使用して画像に調整を適用するユーティリティ
 */

export interface CanvasAdjustmentOptions {
  contrast?: number;       // -100 ~ 100
  brightness?: number;     // -100 ~ 100
  whiteBalance?: 'warm' | 'cool' | 'none';
}

/**
 * 画像URLに対してCanvas調整を適用し、Blobとして返す
 * @param imageUrl - 調整する画像のURL
 * @param options - 調整オプション
 * @returns 調整後の画像Blob
 */
export async function applyCanvasAdjustments(
  imageUrl: string,
  options: CanvasAdjustmentOptions
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.addEventListener('load', () => {
      try {
        // Canvasを作成
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        // 画像を描画
        ctx.drawImage(img, 0, 0);

        // 調整が何も指定されていない場合はそのままBlobに変換
        if (!options.contrast && !options.brightness && options.whiteBalance === 'none') {
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob'));
            }
          }, 'image/jpeg', 0.95);
          return;
        }

        // ImageDataを取得して調整を適用
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const {data} = imageData;

        // 調整パラメータを正規化
        const contrastFactor = options.contrast ? (options.contrast + 100) / 100 : 1;
        const brightnessDelta = options.brightness || 0;

        // ホワイトバランス調整の色温度
        let tempR = 1; const tempG = 1; let tempB = 1;
        if (options.whiteBalance === 'warm') {
          tempR = 1.1;
          tempB = 0.9;
        } else if (options.whiteBalance === 'cool') {
          tempR = 0.9;
          tempB = 1.1;
        }

        // ピクセルごとに調整を適用
        for (let i = 0; i < data.length; i += 4) {
          let r = data[i];
          let g = data[i + 1];
          let b = data[i + 2];

          // 明るさ調整
          r += brightnessDelta;
          g += brightnessDelta;
          b += brightnessDelta;

          // コントラスト調整
          r = ((r - 128) * contrastFactor) + 128;
          g = ((g - 128) * contrastFactor) + 128;
          b = ((b - 128) * contrastFactor) + 128;

          // ホワイトバランス調整
          r *= tempR;
          g *= tempG;
          b *= tempB;

          // 0-255の範囲にクランプ
          data[i] = Math.max(0, Math.min(255, r));
          data[i + 1] = Math.max(0, Math.min(255, g));
          data[i + 2] = Math.max(0, Math.min(255, b));
        }

        // 調整後のImageDataをCanvasに書き戻し
        ctx.putImageData(imageData, 0, 0);

        // BlobとしてCanvasの内容を出力
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob after adjustments'));
          }
        }, 'image/jpeg', 0.95);
      } catch (error) {
        reject(error);
      }
    });

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = imageUrl;
  });
}
